@file:OptIn(DelicateApi::class)

package club.srams.website.pages

import androidx.compose.runtime.Composable
import club.srams.website.HeadlineTextStyle
import club.srams.website.components.layouts.PageLayoutData
import club.srams.website.components.widgets.CarouselFade
import club.srams.website.toSitePalette
import com.varabyte.kobweb.compose.css.AlignContent.Companion.Center
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div

private val HERO_IMAGE_LEFT_MAX_WIDTH = 450.px
private val HERO_IMAGE_RIGHT_MAX_WIDTH = 350.px

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home"))
}

//
//
//

val HomePageHeadlineStyle = CssStyle {
    base { Modifier.fontSize(1.5.cssRem) }   // mobile
    Breakpoint.MD { Modifier.fontSize(2.5.cssRem) } // desktop
}


// Container that has a tagline and grid on desktop, and just the tagline on mobile
val HeroContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(2.cssRem) }
    Breakpoint.MD { Modifier.margin { top(5.vh) } }
}

// A demo grid that appears on the homepage because it looks good
val HomeGridStyle = CssStyle.base {
    Modifier
        .gap(0.5.cssRem)
        .width(70.cssRem)
        .height(18.cssRem)
}

private val GridCellColorVar by StyleVariable<Color>()
val HomeGridCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(GridCellColorVar.value())
        .boxShadow(blurRadius = 0.6.cssRem, color = GridCellColorVar.value())
        .borderRadius(1.cssRem)
}

@Composable
private fun GridCell(color: Color, row: Int, column: Int, width: Int? = null, height: Int? = null) {
    Div(
        HomeGridCellStyle.toModifier()
            .setVariable(GridCellColorVar, color)
            .gridItem(row, column, width, height)
            .toAttrs()
    )
}


@Composable
private fun HomePageCarousel(
    breakpoint: Breakpoint
) {
    Div {
        Column {
            CarouselFade(
                images
                , fadeDurationMs = 800
                , modifier = Modifier
                    .fillMaxWidth()
                    .height(600.px)
                , imageModifier = Modifier
                    .fillMaxWidth()
                    .height(if (breakpoint <= Breakpoint.SM) {
                        300.px
                    } else {
                        600.px
                    })
                    .objectFit(ObjectFit.Cover)
            )
        }
    }

}

@Composable
private fun HomePageHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()
    val textColor = if (ColorMode.current == ColorMode.LIGHT) Colors.Black else Colors.White

    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Welcome to"
                , HomePageHeadlineStyle.toModifier()
                    .color(textColor)
                    .align(alignment = CenterHorizontally)
            )

            SpanText(
                "Shelton Rock and Mineral Society"
                , HomePageHeadlineStyle.toModifier()
                    .color(sitePalette.brand.accent)
                    .textShadow(0.px, 0.px, blurRadius = 0.5.cssRem, color = Colors.Gray)
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun HomePageHero() {
    HomePageHeadline()

    // 1 column on mobile, 2 columns on md+ (text left, image right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle.toModifier()
            .fillMaxWidth().gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {

                Image(
                    src = "/images/members_meeting/2026-05-members-meeting.jpg",
                    alt = "how to identify rocks",
                    modifier = Modifier
                        .maxWidth(HERO_IMAGE_LEFT_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }
        Image(
            src = "/images/home_page/srams_poster.png",
            alt = "SRAMS poster",
            modifier = Modifier
                .maxWidth(HERO_IMAGE_RIGHT_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
    }
}

@Composable
private fun HomePageCodeOfEthicsSection() {
    // Single centered column — image scales with viewport on small screens
    SimpleGrid(
        numColumns = numColumns(1),
        modifier = Modifier.fillMaxWidth()
    ) {
        RockhoundCodeEthicsPage()  // ← generated from RockhoundCodeofEthics.md
    }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    HomePageHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 4.cssRem)
    )
    HomePageCarousel(rememberBreakpoint())
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 4.cssRem)
    )
    HomePageCodeOfEthicsSection()

}




private val images = listOf(
    Pair("/images/home_page/shelton_christian_church.jpg", "Shelton Christian Church"),
    Pair("/images/home_page/agate_1.jpg", "agate 1"),
    Pair("/images/home_page/2025_hunter_farms.jpg", "2025 Hunter Farms"),
    Pair("/images/home_page/slice_on_stand_with_light.jpg", "slice on stand"),
    Pair("/images/field_trips/2025/10_14_dosewallips_state_park/image_1.jpg", "Dosewallips State Park"),
    Pair("/images/home_page/agate_2.jpg", "agate 2"),
    Pair("/images/home_page/Ellensburg_Blue.jpg", "Ellensburg Blue"),
    Pair("/images/home_page/2023_matlock_1.jpg", "Matlock 1"),
    Pair("/images/home_page/spear_point.jpg", "spear point"),
    Pair("/images/home_page/2025_hunter_farms_wheel.jpg", "2025 Hunter Farms"),
    Pair("/images/home_page/red_jasper.jpg", "red jasper"),
    Pair("/images/home_page/2025_critter_party.jpg", "critter party"),
    Pair("/images/home_page/2023_matlock_2.jpg", "Matlock 2"),
    Pair("/images/field_trips/2026/05_16_Saddle_Mountain/image_1.jpg", "Saddle Mountain 1"),
    Pair("/images/field_trips/2026/05_16_Saddle_Mountain/image_2.jpg", "Saddle Mountain 2"),
)
